import { PDFDocument, PDFPage, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { BatteryData } from '../../api/service/BatteryInterfaces';
import PretendardFont from '../../assets/fonts/PretendardVariable.ttf'; // Pretendard 폰트 경로
import PDFlogo from '../../assets/images/PDFlogo.png';
import { SaleStatus } from '../../api/service/UtilsInterface';

const generateDiagnosisTab1PDF = async (data: BatteryData[]) => {
  const formatNumber = (value: number | null | undefined): string => {
    if (value === undefined || value === null || isNaN(value)) return '-';
    return new Intl.NumberFormat('ko-KR').format(value);
  };

  const formatDate = (date: string | undefined): string => {
    if (!date) return '-';
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return '-';
      return parsedDate.toISOString().split('T')[0];
    } catch {
      return '-';
    }
  };

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);

  const fontBytes = await fetch(PretendardFont).then((res) =>
    res.arrayBuffer()
  );
  const pretendardFont = await pdfDoc.embedFont(fontBytes);

  const pageSize: [number, number] = [841.89, 595.28]; // A4 가로 크기
  let page: PDFPage = pdfDoc.addPage(pageSize);

  const { width, height } = page.getSize();
  const fontSize = 10;
  const titleFontSize = 16; // 타이틀 폰트 크기
  const margin = 10; // 좌우 여백
  let yPosition = height;

  // 타이틀 디자인
  const titleHeight = 40; // 타이틀 영역 높이
  const title = '진단 리스트';
  const titleBgColor = rgb(0.137, 0.549, 0.392); // 녹색 배경 (#238C64)
  const titleTextColor = rgb(1, 1, 1); // 흰색 텍스트

  // 타이틀 배경 그리기
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight,
    width: width,
    height: titleHeight,
    color: titleBgColor,
  });

  // 이미지 삽입
  const logoBytes = await fetch(PDFlogo).then((res) => res.arrayBuffer());
  const logoImage = await pdfDoc.embedPng(logoBytes);
  const logoDims = logoImage.scale(0.5);

  // 이미지 위치 계산
  const logoX = margin;
  const logoY = yPosition - titleHeight + (titleHeight - logoDims.height) / 2;
  page.drawImage(logoImage, {
    x: logoX,
    y: logoY,
    width: logoDims.width,
    height: logoDims.height,
  });

  // 텍스트 위치 계산
  const titleX =
    width - margin - pretendardFont.widthOfTextAtSize(title, titleFontSize);
  const titleY = yPosition - titleHeight / 2 - titleFontSize / 2;
  page.drawText(title, {
    x: titleX,
    y: titleY,
    size: titleFontSize,
    font: pretendardFont,
    color: titleTextColor,
  });

  yPosition -= titleHeight + 20;

  // 헤더 정의
  const headers = [
    'NO',
    '매물상태',
    '고유번호',
    '회사명',
    '제조사',
    '차량',
    'SOH',
    'SOC',
    '셀편차',
    '고장코드',
    '측정일',
    '판매금액(만원)',
  ];

  // 열 너비 계산 함수
  const calculateDynamicColumnWidths = (
    font: typeof pretendardFont,
    headers: string[],
    tableData: string[][],
    pageWidth: number,
    margin: number,
    fontSize: number
  ): number[] => {
    const initialWidths = headers.map((header) =>
      font.widthOfTextAtSize(header, fontSize)
    );

    tableData.forEach((row) => {
      row.forEach((cell, i) => {
        const cellWidth = font.widthOfTextAtSize(cell, fontSize);
        if (cellWidth > initialWidths[i]) {
          initialWidths[i] = cellWidth;
        }
      });
    });

    const totalInitialWidth = initialWidths.reduce((acc, w) => acc + w, 0);
    const availableWidth = pageWidth - 2 * margin;

    const scalingFactor = availableWidth / totalInitialWidth;
    return initialWidths.map((w) => w * scalingFactor);
  };

  const calculateColumnPositions = (columnWidths: number[], margin: number) =>
    columnWidths.reduce(
      (acc, width) => [...acc, acc[acc.length - 1] + width],
      [margin]
    );

  const getSaleStatusInKorean = (status: string): string => {
    switch (status) {
      case SaleStatus.NOT_REGISTERED:
        return '미등록';
      case SaleStatus.ON_SALE:
        return '판매 중';
      case SaleStatus.RESERVED:
        return '판매 예약';
      case SaleStatus.FINISHED:
        return '판매 완료';
      default:
        return '-';
    }
  };

  const tableData = data.map((item, index) => [
    (index + 1).toString(),
    getSaleStatusInKorean(item.saleStatus || ''), // saleStatus를 한글로 변환
    item.identifier || '-',
    item.company || '-',
    item.manufacturer || '-',
    item.model || '-',
    item.soh?.toString() || '-',
    item.soc?.toString() || '-',
    item.cellDeviation?.toString() || '-',
    item.failureCodeYN || '-',
    formatDate(item.measurementDate),
    formatNumber(item.price),
  ]);

  const columnWidths = calculateDynamicColumnWidths(
    pretendardFont,
    headers,
    tableData,
    width,
    margin,
    fontSize
  );
  const columnXPositions = calculateColumnPositions(columnWidths, margin);

  const drawHeaderRow = (
    headers: string[],
    y: number,
    headerTextColor: ReturnType<typeof rgb>,
    headerBgColor: ReturnType<typeof rgb>
  ) => {
    page.drawRectangle({
      x: margin,
      y: y - fontSize - 7,
      width: columnXPositions[columnXPositions.length - 1] - margin,
      height: fontSize + 8,
      color: headerBgColor,
    });

    headers.forEach((header, i) => {
      const textWidth = pretendardFont.widthOfTextAtSize(header, fontSize);
      const columnCenter = columnXPositions[i] + columnWidths[i] / 2;
      const textStartX = columnCenter - textWidth / 2;

      page.drawText(header, {
        x: textStartX,
        y: y - fontSize - 2,
        size: fontSize,
        font: pretendardFont,
        color: headerTextColor,
      });
    });
  };

  drawHeaderRow(headers, yPosition, rgb(1, 1, 1), rgb(0.569, 0.776, 0.698));
  yPosition -= fontSize + 12;

  data.forEach((item, index) => {
    if (yPosition < margin + fontSize + 20) {
      page = pdfDoc.addPage(pageSize);
      yPosition = height - margin - 20;
    }

    const row = [
      (index + 1).toString(),
      getSaleStatusInKorean(item.saleStatus || ''), // saleStatus를 한글로 변환
      item.identifier || '-',
      item.company || '-',
      item.manufacturer || '-',
      item.model || '-',
      item.soh?.toString() || '-',
      item.soc?.toString() || '-',
      item.cellDeviation?.toString() || '-',
      item.failureCodeYN || '-',
      formatDate(item.measurementDate),
      formatNumber(item.price),
    ];

    row.forEach((text, i) => {
      const textWidth = pretendardFont.widthOfTextAtSize(text, fontSize);
      const columnCenter = columnXPositions[i] + columnWidths[i] / 2;
      const textStartX = columnCenter - textWidth / 2;

      page.drawText(text, {
        x: textStartX,
        y: yPosition - fontSize - 2,
        size: fontSize,
        font: pretendardFont,
        color: rgb(0, 0, 0),
      });
    });

    yPosition -= fontSize + 10;
  });

  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
};

export default generateDiagnosisTab1PDF;
